import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";

// Keep the same routes and permission keys used by the existing Student master menu.
const MENU_ITEMS = [
  {
    path: "/students/report",
    label: "Reports",
    icon: "ri-bar-chart-line",
    activePaths: ["/students/report"],
  },
  {
    path: "/students",
    label: "Students",
    icon: "ri-group-line",
    activePaths: [
      "/students",
      "/student/update",
      "/student/add",
      "/student/details",
    ],
  },
  {
    path: "/duplicate/students",
    label: "Duplicates",
    icon: "ri-file-copy-line",
    activePaths: ["/duplicate/students"],
  },
  {
    path: "/student/app/login/status",
    label: "App Login",
    icon: "ri-smartphone-line",
    activePaths: ["/student/app/login/status"],
  },
  {
    path: "/student/admission/form/print",
    label: "Admission",
    icon: "ri-file-text-line",
    activePaths: ["/student/admission/form/print"],
  },
  {
    path: "/student/demo/form/print",
    label: "Demo Form",
    icon: "ri-presentation-line",
    activePaths: ["/student/demo/form/print"],
  },
  {
    path: "/students/import/v1",
    label: "Bulk Tools",
    icon: "ri-tools-line",
    activePaths: [
      "/students/import/v1",
      "/students/import",
      "/students/import/batch-enroll/v1",
      "/students/discount/offer/set",
      "/students/update/fee",
      "/students/update/bus-fee",
      "/bulk/student/subjects/update",
      "/students/import/profile-picture",
    ],
    subItems: [
      {
        path: "/students/import/v1",
        label: "Import Students V1",
        description: "Latest student import",
        icon: "ri-upload-cloud-2-line",
      },
      {
        path: "/students/import",
        label: "Import Students V0",
        description: "Legacy student import",
        icon: "ri-upload-cloud-line",
      },
      {
        path: "/students/import/batch-enroll/v1",
        label: "Batch Enroll",
        description: "Bulk batch enrollment",
        icon: "ri-user-add-line",
      },
      {
        path: "/students/discount/offer/set",
        label: "Discount Offer",
        description: "Assign student discounts",
        icon: "ri-price-tag-3-line",
      },
      {
        path: "/students/update/fee",
        label: "Fee Update",
        description: "Bulk student fee update",
        icon: "ri-money-rupee-circle-line",
      },
      {
        path: "/students/update/bus-fee",
        label: "Bus Fee Update",
        description: "Bulk transport fee update",
        icon: "ri-bus-2-line",
      },
      {
        path: "/bulk/student/subjects/update",
        label: "Subjects Update",
        description: "Bulk subject mapping",
        icon: "ri-book-open-line",
      },
      {
        path: "/students/import/profile-picture",
        label: "Profile Pictures",
        description: "Import student profile photos",
        icon: "ri-image-add-line",
      },
    ],
  },
];

const normalize = (path = "") => String(path || "/").replace(/\/+$/, "") || "/";
const activeFor = (item, pathname) => {
  const current = normalize(pathname);
  return (item.activePaths || [item.path]).some((path) => {
    const route = normalize(path);
    return current === route;
  });
};

export default function Master() {
  return;
  const { pathname } = useLocation();
  const hasAccess = usePrivateLink();
  const scrollerRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [arrows, setArrows] = useState({ left: false, right: false });
  const [position, setPosition] = useState({ top: 80, left: 16, width: 480 });

  // Re-evaluate when permissions or route change; don't hide child tools just
  // because access to the Bulk Tools landing route is denied.
  const visibleItems = MENU_ITEMS.reduce((result, item) => {
    if (!item.subItems) {
      if (hasAccess(item.path)) result.push(item);
      return result;
    }

    const subItems = item.subItems.filter((child) => hasAccess(child.path));

    if (subItems.length > 0) {
      result.push({ ...item, subItems });
    }

    return result;
  }, []);
  const bulkItem = visibleItems.find((item) => item.subItems?.length);
  const currentItem = visibleItems.find((item) => activeFor(item, pathname));

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setArrows({
      left: el.scrollLeft > 5,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 5,
    });
  };

  const updatePopoverPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(500, Math.max(280, window.innerWidth - 24));
    const left = Math.max(
      12,
      Math.min(rect.right - width, window.innerWidth - width - 12),
    );
    const estimatedHeight = Math.min(480, window.innerHeight * 0.72);
    const below = window.innerHeight - rect.bottom;
    const top =
      below >= 240 || below >= estimatedHeight
        ? rect.bottom + 9
        : Math.max(12, rect.top - estimatedHeight - 9);
    setPosition({ top, left, width });
  };

  useEffect(() => {
    setBulkOpen(false);
  }, [pathname]);

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return undefined;
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateArrows)
        : null;
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    window.addEventListener("resize", updateArrows);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateArrows);
    };
  }, [visibleItems.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    const active = el?.querySelector('[aria-current="page"]');
    if (el && active) {
      const target =
        active.offsetLeft -
        el.offsetLeft -
        (el.clientWidth - active.clientWidth) / 2;
      el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    }
  }, [pathname]);

  useEffect(() => {
    if (!bulkOpen) return undefined;
    updatePopoverPosition();
    const dismiss = (event) => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !popoverRef.current?.contains(event.target)
      )
        setBulkOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") setBulkOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
    };
  }, [bulkOpen]);

  if (!visibleItems.length) return null;

  return (
    <>
      <div className="col-12 smv2-container">
        <nav className="smv2-bar" aria-label="Student management navigation">
          <div className="smv2-brand">
            <span className="smv2-brand-icon">
              <i className="ri-graduation-cap-line" />
            </span>
            <span className="smv2-brand-copy">
              <strong>Student Management</strong>
              <small>Quick navigation</small>
            </span>
          </div>
          <span className="smv2-separator" />
          {arrows.left && (
            <button
              type="button"
              className="smv2-scroll-arrow"
              aria-label="Scroll tabs left"
              onClick={() =>
                scrollerRef.current?.scrollBy({
                  left: -240,
                  behavior: "smooth",
                })
              }
            >
              <i className="ri-arrow-left-s-line" />
            </button>
          )}
          <div
            className="smv2-scroller"
            ref={scrollerRef}
            onScroll={updateArrows}
          >
            {visibleItems.map((item) => {
              const active = activeFor(item, pathname);
              if (item.subItems)
                return (
                  <button
                    key={item.path}
                    ref={triggerRef}
                    type="button"
                    className={`smv2-tab smv2-bulk ${active || bulkOpen ? "active" : ""}`}
                    onClick={() => {
                      if (!bulkOpen) updatePopoverPosition();
                      setBulkOpen((v) => !v);
                    }}
                    aria-expanded={bulkOpen}
                    aria-haspopup="dialog"
                    aria-current={active ? "page" : undefined}
                  >
                    <i className={item.icon} />
                    <span>{item.label}</span>
                    <span className="smv2-count">{item.subItems.length}</span>
                    <i
                      className={
                        bulkOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                      }
                    />
                  </button>
                );
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`smv2-tab ${active ? "active" : ""}`}
                  aria-current={active ? "page" : undefined}
                >
                  <i className={item.icon} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
          {arrows.right && (
            <button
              type="button"
              className="smv2-scroll-arrow"
              aria-label="Scroll tabs right"
              onClick={() =>
                scrollerRef.current?.scrollBy({ left: 240, behavior: "smooth" })
              }
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          )}
          {currentItem && (
            <span
              className="smv2-current"
              title={`Current section: ${currentItem.label}`}
            >
              <i className="ri-checkbox-circle-line" /> {currentItem.label}
            </span>
          )}
        </nav>
      </div>

      {bulkOpen &&
        bulkItem &&
        createPortal(
          <>
            <div
              className="smv2-backdrop"
              onClick={() => setBulkOpen(false)}
              aria-hidden="true"
            />
            <section
              ref={popoverRef}
              className="smv2-popover"
              role="dialog"
              aria-modal="false"
              aria-label="Bulk student operations"
              style={{
                top: position.top,
                left: position.left,
                width: position.width,
              }}
            >
              <div className="smv2-popover-head">
                <div className="smv2-popover-heading">
                  <span className="smv2-popover-logo">
                    <i className="ri-tools-line" />
                  </span>
                  <div>
                    <strong>Bulk Operations</strong>
                    <small>Choose a student management tool</small>
                  </div>
                </div>
                <div className="smv2-popover-controls">
                  <span className="smv2-tool-count">
                    {bulkItem.subItems.length} tools
                  </span>
                  <button
                    type="button"
                    className="smv2-close"
                    onClick={() => setBulkOpen(false)}
                    aria-label="Close bulk tools"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              </div>
              <div className="smv2-popover-grid">
                {bulkItem.subItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setBulkOpen(false)}
                    className={`smv2-tool ${activeFor(item, pathname) ? "active" : ""}`}
                  >
                    <span className="smv2-tool-icon">
                      <i className={item.icon} />
                    </span>
                    <span className="smv2-tool-copy">
                      <strong>{item.label}</strong>
                      <small>{item.description}</small>
                    </span>
                    <i className="ri-arrow-right-s-line smv2-tool-chevron" />
                  </Link>
                ))}
              </div>
            </section>
          </>,
          document.body,
        )}
      <style>{`
        .smv2-container, .smv2-popover { --smv2-primary: var(--bs-primary,#696cff); --smv2-bg:var(--bs-card-bg,#fff); --smv2-text:var(--bs-body-color,#384154); --smv2-border:var(--bs-border-color,#e6e9f1); --smv2-muted:var(--bs-secondary-color,#79849a); --smv2-soft:rgba(105,108,255,.09); }
        .smv2-container {min-width:0;margin-bottom:15px}
        .smv2-bar {display:flex;align-items:center;gap:9px;min-width:0;min-height:66px;padding:9px 12px;background:var(--smv2-bg);border:1px solid var(--smv2-border);border-radius:14px;box-shadow:0 4px 20px rgba(28,39,69,.045)}
        .smv2-brand {display:flex;align-items:center;gap:10px;flex-shrink:0}
        .smv2-brand-icon {width:41px;height:41px;border-radius:11px;display:grid;place-items:center;color:white;background:linear-gradient(135deg,#6d7df0,#5365d2);font-size:21px}
        .smv2-brand-copy {display:flex;flex-direction:column;gap:2px;white-space:nowrap}.smv2-brand-copy strong{font-size:13px;color:var(--smv2-text);font-weight:750}.smv2-brand-copy small{font-size:10px;color:var(--smv2-muted)}
        .smv2-separator {width:1px;height:32px;background:var(--smv2-border);flex-shrink:0;margin:0 3px}
        .smv2-scroller{display:flex;gap:5px;flex:1;align-items:center;min-width:0;overflow-x:auto;scrollbar-width:none;scroll-behavior:smooth;padding:2px}.smv2-scroller::-webkit-scrollbar{display:none}
        .smv2-tab{flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:42px;padding:10px 13px;border:1px solid transparent;background:transparent;border-radius:9px;color:var(--smv2-muted);font-family:inherit;font-size:12px;font-weight:650;white-space:nowrap;cursor:pointer;text-decoration:none!important;transition:background .15s,color .15s,border-color .15s}
        .smv2-tab>i:first-child{font-size:18px}.smv2-tab:hover{background:var(--smv2-soft);color:var(--smv2-primary);border-color:var(--smv2-border)}
        .smv2-tab.active{background:linear-gradient(135deg,#6577e6,#5264d2);color:#fff;border-color:transparent;box-shadow:0 3px 9px rgba(83,101,210,.18)}.smv2-tab.active:hover{color:#fff}
        .smv2-count{border-radius:20px;padding:2px 6px;font-size:10px;background:rgba(105,108,255,.12);color:var(--smv2-primary)}.smv2-tab.active .smv2-count{background:rgba(255,255,255,.22);color:white}
        .smv2-scroll-arrow,.smv2-close{flex-shrink:0;border:1px solid var(--smv2-border);background:var(--smv2-bg);color:var(--smv2-muted);display:grid;place-items:center;border-radius:8px;cursor:pointer}.smv2-scroll-arrow{width:30px;height:35px;font-size:19px}.smv2-scroll-arrow:hover,.smv2-close:hover{color:var(--smv2-primary);background:var(--smv2-soft)}
        .smv2-current{flex-shrink:0;display:flex;align-items:center;gap:5px;font-size:11px;color:var(--smv2-primary);font-weight:700;padding:8px 9px;background:var(--smv2-soft);border-radius:8px;white-space:nowrap}
        .smv2-popover {position:fixed;z-index:20000;max-height:min(72vh,510px);display:flex;flex-direction:column;border:1px solid var(--smv2-border);background:var(--smv2-bg);border-radius:15px;overflow:hidden;box-shadow:0 18px 55px rgba(20,30,65,.22)}
        .smv2-popover-head {display:flex;align-items:center;justify-content:space-between;gap:12px;padding:15px 16px;border-bottom:1px solid var(--smv2-border)}.smv2-popover-heading{display:flex;align-items:center;gap:11px}.smv2-popover-logo{width:38px;height:38px;display:grid;place-items:center;border-radius:10px;background:var(--smv2-soft);color:var(--smv2-primary);font-size:20px}.smv2-popover-heading>div{display:flex;flex-direction:column;gap:3px}.smv2-popover-heading strong{font-size:14px;color:var(--smv2-text)}.smv2-popover-heading small{font-size:11px;color:var(--smv2-muted)}
        .smv2-popover-controls{display:flex;align-items:center;gap:9px}.smv2-tool-count{font-size:11px;font-weight:700;color:var(--smv2-primary);background:var(--smv2-soft);padding:6px 9px;border-radius:7px}.smv2-close{height:30px;width:30px;font-size:18px}
        .smv2-popover-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;padding:12px;overflow-y:auto;overscroll-behavior:contain}.smv2-tool{display:flex;align-items:center;gap:10px;min-width:0;padding:12px 10px;border:1px solid var(--smv2-border);border-radius:11px;color:var(--smv2-text);text-decoration:none!important;transition:background .15s,border-color .15s}.smv2-tool:hover,.smv2-tool.active{color:var(--smv2-primary);background:var(--smv2-soft);border-color:var(--smv2-primary)}.smv2-tool-icon{display:grid;place-items:center;flex-shrink:0;width:37px;height:37px;border-radius:10px;background:var(--smv2-soft);color:var(--smv2-primary);font-size:19px}.smv2-tool-copy{flex:1;min-width:0;display:flex;flex-direction:column;gap:4px}.smv2-tool-copy strong{font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.smv2-tool-copy small{font-size:10.5px;color:var(--smv2-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.smv2-tool-chevron{font-size:17px;color:var(--smv2-muted)}
        .smv2-backdrop{display:none}.smv2-tab:focus-visible,.smv2-scroll-arrow:focus-visible,.smv2-tool:focus-visible,.smv2-close:focus-visible{outline:2px solid var(--smv2-primary);outline-offset:2px}
        [data-bs-theme="dark"] .smv2-popover{--smv2-bg:#2b2c40;--smv2-text:#e6e7f0;--smv2-muted:#aaaec2;--smv2-border:#474961}
        @media(max-width:1200px){.smv2-brand-copy small,.smv2-current{display:none}.smv2-bar{gap:6px}}
        @media(max-width:767px){.smv2-brand-copy,.smv2-separator{display:none}.smv2-container{margin-bottom:12px}.smv2-bar{padding:7px;min-height:54px;border-radius:11px}.smv2-brand-icon{width:36px;height:36px;font-size:18px}.smv2-tab{min-height:37px;padding:8px 11px;font-size:11.5px}.smv2-tab>i:first-child{font-size:16px}.smv2-popover{top:auto!important;bottom:10px;left:10px!important;width:calc(100vw - 20px)!important;max-height:calc(100dvh - 20px);border-radius:15px}.smv2-backdrop{display:block;position:fixed;inset:0;background:rgba(15,23,42,.43);z-index:19999}.smv2-popover-grid{grid-template-columns:1fr}.smv2-tool{padding:11px}.smv2-popover-head{padding:13px}}
        @media(prefers-reduced-motion:reduce){.smv2-scroller{scroll-behavior:auto}.smv2-tab,.smv2-tool{transition:none}}
        @media print {.smv2-container,.smv2-popover,.smv2-backdrop{display:none!important}}
      `}</style>
    </>
  );
}
